import { FastField, Formik } from 'formik';
import KTFormGroup from 'general/components/OtherKeenComponents/Forms/KTFormGroup';
import KTFormInput, { KTFormInputType } from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import _ from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import Utils from 'general/utils/Utils';
import AppColors from 'general/constants/AppColors';
import { useTranslation } from 'react-i18next';
import { AppDialogHelper } from 'general/components/AppDialog';
import AppResource from 'general/constants/AppResource';
import AppConfigs from 'general/constants/AppConfigs';

ForgotPasswordForm.propTypes = {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

function ForgotPasswordForm(props) {
  const { onSubmit, onCancel } = props;
  const { t } = useTranslation();

  const [requestCount, setRequestCount] = useState(() => parseInt(localStorage.getItem('requestCount'), 10) || 0);
  const [remainingTime, setRemainingTime] = useState(() => {
    const storedRemainingTime = parseInt(localStorage.getItem('remainingTime'), 10) || 0;
    const storedStartTime = localStorage.getItem('startTime');
    if (storedStartTime && storedRemainingTime > 0) {
      const elapsedTime = Math.floor((Date.now() - parseInt(storedStartTime, 10)) / 1000);
      const newRemainingTime = storedRemainingTime - elapsedTime;
      return newRemainingTime > 0 ? newRemainingTime : 0;
    }
    return 0;
  });

  useEffect(() => {
    const updateRemainingTime = () => {
      const storedStartTime = localStorage.getItem('startTime');
      const storedRemainingTime = parseInt(localStorage.getItem('remainingTime'), 10) || 0;
      if (storedStartTime) {
        const timeSinceStart = Math.floor((Date.now() - parseInt(storedStartTime, 10)) / 1000);
        if (timeSinceStart >= 86400) {
          setRequestCount(0);
          localStorage.removeItem('requestCount');
        }

        const elapsedTime = Math.floor((Date.now() - parseInt(storedStartTime, 10)) / 1000);
        const newRemainingTime = storedRemainingTime - elapsedTime;

        if (newRemainingTime > 0) {
          setRemainingTime(newRemainingTime);
        } else {
          setRemainingTime(0);
          if (requestCount > 5) {
            setRequestCount(0);
            localStorage.setItem('requestCount', '0');
          }
          localStorage.removeItem('remainingTime');
        }
      } else {
        setRemainingTime(0);
      }
    };

    updateRemainingTime();

    let timer;
    if (remainingTime > 0) {
      timer = setInterval(updateRemainingTime, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [remainingTime, requestCount]);

  const handleTooManyRequestsDialog = () => {
    AppDialogHelper.show(
      t('Cảnh báo'),
      t('Bạn đã yêu cầu quá nhiều lần. Vui lòng thử lại sau 15 phút hoặc liên hệ đội ngũ Admin để được hỗ trợ.'),
      [
        {
          title: t('Tôi đã hiểu'),
          type: 'neutral',
          onClick: () => AppDialogHelper.hide(),
        },
      ],
      false,
      AppResource.images.dialogIcons.dialogNotice
    );
  };

  const handleSubmit = async (values) => {
    const newCount = requestCount + 1;

    if (newCount > 5) {
      handleTooManyRequestsDialog();
      setRequestCount(newCount);
      setRemainingTime(15 * 60);
      localStorage.setItem('requestCount', newCount.toString());
      localStorage.setItem('startTime', Date.now().toString());
      localStorage.setItem('remainingTime', (15 * 60).toString());
      return;
    }

    const result = await onSubmit(values);
    if (result === 'success') {
      setRequestCount(newCount);
      setRemainingTime(3 * 60);
      localStorage.setItem('requestCount', newCount.toString());
      localStorage.setItem('startTime', Date.now().toString());
      localStorage.setItem('remainingTime', (3 * 60).toString());
    }
  };

  const handleCancel = () => onCancel && onCancel();

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isBlocked = remainingTime > 0;

  return (
    <div
      className="login-form"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.1 }}
    >
      <Formik
        initialValues={{ email: '' }}
        validationSchema={Yup.object({
          email: Yup.string().required(t('emailRequired'))
            .test('len', t('Email không hợp lệ', { field: 'email' }), (val) => {
              if (val) {
                return (
                  !val.toString().includes(' ') &&
                  val.toString().match(AppConfigs.regex.email)
                )
              }
              return false;
            }),
        })}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <form
            className="form fv-plugins-bootstrap fv-plugins-framework"
            onSubmit={formikProps.handleSubmit}
            noValidate
          >
            <div className="pt-lg-0 pt-5 text-center">
              <h3
                className="font-weight-boldest"
                style={{ fontSize: Utils.returnFontSize(30), color: AppColors.gray[900] }}
              >
                {t('Quên mật khẩu')}
              </h3>
              <p
                className="font-weight-bolder mb-2"
                style={{ fontSize: Utils.returnFontSize(16), color: AppColors.gray[500] }}
              >
                {t('Nhập email của bạn để nhận thông tin đặt lại mật khẩu')}
              </p>
            </div>

            {isBlocked && (
              <div className="d-flex justify-content-center">
                <div className="text-danger text-center max-w-375px">
                  {requestCount < 6 ? (
                    <>
                      {t('Bạn vừa yêu cầu đặt lại mật khẩu. Vui lòng kiểm tra hộp thư. Bạn có thể gửi yêu cầu tiếp theo sau: ')}
                      <span className="font-weight-boldest">{formatTime(remainingTime)}</span> {t(' phút.')}
                    </>
                  ) : (
                    <>
                      {t('Bạn đã yêu cầu quá nhiều lần. Vui lòng thử lại sau ')}
                      <span className="font-weight-boldest">{formatTime(remainingTime)}</span>
                      {t(' phút hoặc liên hệ đội ngũ Admin để được hỗ trợ.')}
                    </>
                  )}
                </div>
              </div>
            )}

            <KTFormGroup
              label={t('Email')}
              inputName="email"
              additionalClassName="fv-plugins-icon-container mb-4 pt-8"
              additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
              isRequired
              inputElement={
                <FastField name="email">
                  {({ field, form, meta }) => (
                    <KTFormInput
                      name={field.name}
                      value={field.value}
                      onChange={(value) => form.setFieldValue(field.name, value)}
                      onBlur={() => form.setFieldTouched(field.name, true)}
                      enableCheckValid
                      isValid={_.isEmpty(meta.error)}
                      isTouched={meta.touched}
                      feedbackText={meta.error}
                      type={KTFormInputType.email}
                      placeholder={t('Nhập email')}
                      solidBackground
                      additionalInputClassName="h-55px"
                    />
                  )}
                </FastField>
              }
            />

            <div className="form-group d-flex flex-wrap gap-3 pb-lg-0">
              <button
                type="button"
                className="btn btn-light font-weight-bolder font-size-h6 px-8 py-4 my-3 flex-grow-1 d-flex align-items-center justify-content-center"
                onClick={handleCancel}
              >
                {t('Hủy bỏ')}
              </button>
              <button
                type="submit"
                className={`btn ${isBlocked ? 'btn-light' : 'btn-success'} font-weight-bolder font-size-h6 px-8 py-4 my-3 flex-grow-1 d-flex align-items-center justify-content-center`}
                disabled={isBlocked}
              >
                {t('Gửi yêu cầu')}
              </button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}

export default ForgotPasswordForm;